import React, { useState, useEffect, useRef, useContext, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import apiClient from "../api/client";
import { 
  Video, VideoOff, Mic, MicOff, PhoneOff, 
  ArrowLeft, AlertTriangle, Clock, Timer, Sparkles,
  Maximize2, Minimize2
} from 'lucide-react';
import { toast } from 'sonner';
import { AuthContext } from '../App';
import { getDisplayName, getInitial } from '../utils/safeProfile';
import { useMediaUsage } from '../hooks/useMediaUsage';
import { Progress } from './ui/progress';


// Warning threshold in seconds (2 minutes)
const WARNING_THRESHOLD_SECONDS = 120;

const VideoChat = () => {
  const { matchId } = useParams();
  const { user, token } = useContext(AuthContext);
  const navigate = useNavigate();
  
  // 🎯 PATCH: Ref for true browser fullscreen container
  const containerRef = useRef(null);
  
  // Media usage hook
  const { 
    loading: usageLoading, 
    canStartVideoCall,
    effectiveVideoSessionSeconds,
    videoDailyRemainingSeconds,
    mediaTier,
    refetch: refetchUsage,
  } = useMediaUsage();
  
  // Video chat state
  const [isVideoOn, setIsVideoOn] = useState(true);
  const [isAudioOn, setIsAudioOn] = useState(true);
  const [isCallActive, setIsCallActive] = useState(false);
  const [isConnecting, setIsConnecting] = useState(false);
  const [match, setMatch] = useState(null);
  const [callDuration, setCallDuration] = useState(0);
  
  // Media cap state
  const [sessionTimeRemaining, setSessionTimeRemaining] = useState(0);
  const [hasShownWarning, setHasShownWarning] = useState(false);
  const [limitReason, setLimitReason] = useState(null); // 'session' | 'daily' | null
  
  // 🎯 PATCH: Browser fullscreen state
  const [isBrowserFullscreen, setIsBrowserFullscreen] = useState(false);
  
  // WebRTC refs
  const localVideoRef = useRef(null);
  const remoteVideoRef = useRef(null);
  const localStreamRef = useRef(null);
  const peerConnectionRef = useRef(null);
  const callStartTimeRef = useRef(null);

  // 🎯 PATCH: Listen for browser fullscreen changes
  useEffect(() => {
    const onFsChange = () => setIsBrowserFullscreen(!!document.fullscreenElement);
    document.addEventListener("fullscreenchange", onFsChange);
    return () => document.removeEventListener("fullscreenchange", onFsChange);
  }, []);

  // 🎯 PATCH: Toggle true browser fullscreen
  const toggleBrowserFullscreen = async () => {
    try {
      if (document.fullscreenElement) {
        await document.exitFullscreen();
      } else if (containerRef.current?.requestFullscreen) {
        await containerRef.current.requestFullscreen();
      }
    } catch (e) {
      console.error("Fullscreen failed:", e);
      toast.error("Fullscreen not supported on this device");
    }
  };

  useEffect(() => {
    const fetchMatchData = async () => {
      try {
        const matchesResponse = await apiClient.get(`/matches`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        const currentMatch = matchesResponse.data.find(m => m.match_id === matchId);
        setMatch(currentMatch);
      } catch (error) {
        console.error('Failed to fetch match data:', error);
        navigate('/matches');
      }
    };

    fetchMatchData();
  }, [matchId, token, navigate]);

  // Initialize session time remaining when usage data loads
  useEffect(() => {
    if (!usageLoading && effectiveVideoSessionSeconds > 0) {
      setSessionTimeRemaining(effectiveVideoSessionSeconds);
    }
  }, [usageLoading, effectiveVideoSessionSeconds]);

  // Timer for call duration and session countdown
  useEffect(() => {
    let interval;
    if (isCallActive && callStartTimeRef.current) {
      interval = setInterval(() => {
        const now = Date.now();
        const duration = Math.floor((now - callStartTimeRef.current) / 1000);
        setCallDuration(duration);
        
        // Calculate remaining session time
        const remaining = Math.max(0, effectiveVideoSessionSeconds - duration);
        setSessionTimeRemaining(remaining);
        
        // Show warning toast when 2 minutes remain (only once)
        if (remaining <= WARNING_THRESHOLD_SECONDS && remaining > 0 && !hasShownWarning) {
          setHasShownWarning(true);
          toast.warning('2 minutes remaining', {
            description: 'Your video call session will end soon.',
            duration: 5000,
            icon: <Timer className="w-5 h-5" />,
          });
        }
        
        // Auto-end call when session limit reached
        if (remaining <= 0) {
          setLimitReason('session');
          endCallDueToLimit('session');
        }
      }, 1000);
    }
    
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [isCallActive, effectiveVideoSessionSeconds, hasShownWarning]);

  const formatDuration = (seconds) => {
    const minutes = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const formatTimeRemaining = (seconds) => {
    if (seconds <= 0) return '0:00';
    const minutes = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${minutes}:${secs.toString().padStart(2, '0')}`;
  };

  const initializeMedia = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: true,
        audio: true
      });
      
      localStreamRef.current = stream;
      if (localVideoRef.current) {
        localVideoRef.current.srcObject = stream;
      }
      
      return stream;
    } catch (error) {
      console.error('Error accessing media devices:', error);
      toast.error('Camera access denied', {
        description: 'Please check your camera and microphone permissions.',
      });
      throw error;
    }
  };

  const createPeerConnection = () => {
    const configuration = {
      iceServers: [
        { urls: 'stun:stun.l.google.com:19302' },
        { urls: 'stun:stun1.l.google.com:19302' }
      ]
    };

    const peerConnection = new RTCPeerConnection(configuration);
    
    peerConnection.onicecandidate = (event) => {
      if (event.candidate) {
        console.log('ICE candidate:', event.candidate);
      }
    };

    peerConnection.ontrack = (event) => {
      if (remoteVideoRef.current) {
        remoteVideoRef.current.srcObject = event.streams[0];
      }
    };

    return peerConnection;
  };

  const startCall = async () => {
    // Check if user can start a call
    if (!canStartVideoCall) {
      toast.error('Daily limit reached', {
        description: 'You\'ve used all your video minutes for today. Come back tomorrow!',
        duration: 5000,
      });
      return;
    }
    
    setIsConnecting(true);
    setHasShownWarning(false);
    setLimitReason(null);
    
    try {
      // Initialize local media
      const stream = await initializeMedia();
      
      // Create peer connection
      const peerConnection = createPeerConnection();
      peerConnectionRef.current = peerConnection;
      
      // Add local stream tracks to peer connection
      stream.getTracks().forEach(track => {
        peerConnection.addTrack(track, stream);
      });

      // For MVP, simulate a call starting
      setTimeout(() => {
        setIsConnecting(false);
        setIsCallActive(true);
        callStartTimeRef.current = Date.now();
        setSessionTimeRemaining(effectiveVideoSessionSeconds);
        
        // Simulate remote video (for demo purposes)
        if (remoteVideoRef.current) {
          remoteVideoRef.current.srcObject = stream;
        }
        
        // Show session limit info
        const sessionMinutes = Math.floor(effectiveVideoSessionSeconds / 60);
        toast.info(`Session started`, {
          description: `You have ${sessionMinutes} minutes for this call.`,
          duration: 3000,
        });
      }, 2000);
      
    } catch (error) {
      setIsConnecting(false);
      console.error('Failed to start call:', error);
    }
  };

  const endCallDueToLimit = useCallback((reason) => {
    // Stop local stream
    if (localStreamRef.current) {
      localStreamRef.current.getTracks().forEach(track => track.stop());
    }
    
    // Close peer connection
    if (peerConnectionRef.current) {
      peerConnectionRef.current.close();
    }
    
    // Reset state
    setIsCallActive(false);
    setIsConnecting(false);
    setCallDuration(0);
    callStartTimeRef.current = null;
    
    // Show appropriate message
    if (reason === 'session') {
      toast.info('Session limit reached', {
        description: 'This call has ended. You can start a new call if you have daily minutes remaining.',
        duration: 5000,
        icon: <Clock className="w-5 h-5" />,
      });
    } else if (reason === 'daily') {
      toast.warning('Daily limit reached', {
        description: 'You\'ve used all your video minutes for today. Upgrade for more time!',
        duration: 6000,
        icon: <Sparkles className="w-5 h-5" />,
      });
    }
    
    // Refetch usage after call ends
    refetchUsage();
  }, [refetchUsage]);

  const endCall = async () => {
    // Stop local stream
    if (localStreamRef.current) {
      localStreamRef.current.getTracks().forEach(track => track.stop());
    }
    
    // Close peer connection
    if (peerConnectionRef.current) {
      peerConnectionRef.current.close();
    }
    
    // Reset state
    setIsCallActive(false);
    setIsConnecting(false);
    setCallDuration(0);
    callStartTimeRef.current = null;
    setHasShownWarning(false);
    setLimitReason(null);
    
    // Refetch usage
    refetchUsage();
    
    // Navigate back to chat
    navigate(`/chat/${matchId}`);
  };

  const toggleVideo = () => {
    if (localStreamRef.current) {
      const videoTrack = localStreamRef.current.getVideoTracks()[0];
      if (videoTrack) {
        videoTrack.enabled = !videoTrack.enabled;
        setIsVideoOn(videoTrack.enabled);
      }
    }
  };

  const toggleAudio = () => {
    if (localStreamRef.current) {
      const audioTrack = localStreamRef.current.getAudioTracks()[0];
      if (audioTrack) {
        audioTrack.enabled = !audioTrack.enabled;
        setIsAudioOn(audioTrack.enabled);
      }
    }
  };

  const handleBackToChat = () => {
    if (isCallActive || isConnecting) {
      endCall();
    } else {
      navigate(`/chat/${matchId}`);
    }
  };

  // Calculate progress percentage for session timer
  const sessionProgress = effectiveVideoSessionSeconds > 0 
    ? (sessionTimeRemaining / effectiveVideoSessionSeconds) * 100 
    : 0;
  
  // Determine timer color based on remaining time
  const getTimerColor = () => {
    if (sessionTimeRemaining <= WARNING_THRESHOLD_SECONDS) return 'text-red-400';
    if (sessionTimeRemaining <= 300) return 'text-yellow-400'; // 5 minutes
    return 'text-white';
  };

  const getProgressColor = () => {
    if (sessionTimeRemaining <= WARNING_THRESHOLD_SECONDS) return 'bg-red-500';
    if (sessionTimeRemaining <= 300) return 'bg-yellow-500';
    return 'bg-green-500';
  };

  if (!match) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-warm">
        <div className="text-2xl font-playfair text-rose-600">Loading...</div>
      </div>
    );
  }

  return (
    <div ref={containerRef} className="min-h-screen bg-gray-900 relative">
      {/* Header */}
      <div className="absolute top-0 left-0 right-0 z-50 bg-black/80">
        <div className="flex items-center justify-between p-4">
          <button
            onClick={handleBackToChat}
            className="flex items-center space-x-2 text-white hover:text-gray-300"
            data-testid="back-to-chat-btn"
          >
            <ArrowLeft className="w-5 h-5" />
            <span>Back to Chat</span>
          </button>
          
          <div className="text-center text-white">
            <div className="font-playfair font-semibold">
              {getDisplayName(match?.profile)}
            </div>
            {isCallActive && (
              <div className="flex items-center justify-center gap-2 text-sm">
                <span className="text-gray-300">{formatDuration(callDuration)}</span>
                <span className="text-gray-500">|</span>
                <span className={`flex items-center gap-1 ${getTimerColor()}`} data-testid="session-timer">
                  <Timer className="w-4 h-4" />
                  {formatTimeRemaining(sessionTimeRemaining)}
                </span>
              </div>
            )}
          </div>

          {/* 🎯 PATCH: Fullscreen toggle button */}
          <div className="flex items-center gap-2">
            <button
              onClick={toggleBrowserFullscreen}
              className="p-2 text-white hover:text-gray-300 hover:bg-white/10 rounded-lg transition-colors"
              title={isBrowserFullscreen ? "Exit fullscreen" : "Enter fullscreen"}
              data-testid="video-fullscreen-btn"
            >
              {isBrowserFullscreen ? <Minimize2 className="w-5 h-5" /> : <Maximize2 className="w-5 h-5" />}
            </button>
          </div>
        </div>
        
        {/* Session Progress Bar */}
        {isCallActive && (
          <div className="px-4 pb-2">
            <div className="relative h-1 bg-gray-700 rounded-full overflow-hidden">
              <div 
                className={`absolute left-0 top-0 h-full transition-all duration-1000 ${getProgressColor()}`}
                style={{ width: `${sessionProgress}%` }}
                data-testid="session-progress-bar"
              />
            </div>
          </div>
        )}
      </div>

      {/* Video Container */}
      <div className="relative w-full h-screen">
        {/* Remote Video (Full Screen) */}
        <video
          ref={remoteVideoRef}
          autoPlay
          playsInline
          muted={false}
          className={`w-full h-full object-cover bg-gray-800 ${
            !isCallActive ? 'hidden' : ''
          }`}
          data-testid="remote-video"
        />
        
        {/* Local Video (Picture-in-Picture) */}
        <div className={`absolute bottom-20 right-4 w-48 h-36 rounded-lg overflow-hidden bg-gray-700 border-2 border-white shadow-lg ${
          !isCallActive && !isConnecting ? 'hidden' : ''
        }`}>
          <video
            ref={localVideoRef}
            autoPlay
            playsInline
            muted
            className="w-full h-full object-cover"
            data-testid="local-video"
          />
          {!isVideoOn && (
            <div className="absolute inset-0 bg-gray-800 flex items-center justify-center">
              <VideoOff className="w-8 h-8 text-gray-400" />
            </div>
          )}
        </div>

        {/* Call Not Started State */}
        {!isCallActive && !isConnecting && (
          <div className="absolute inset-0 bg-gradient-to-br from-rose-600/20 to-purple-900/20 flex items-center justify-center">
            <div className="text-center text-white max-w-md px-4">
              <div className="w-24 h-24 bg-rose-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <span className="text-3xl font-bold text-gray-700">
                  {getInitial(match?.profile)}
                </span>
              </div>
              
              <h2 className="font-playfair text-2xl font-bold mb-2">
                Video Chat with {getDisplayName(match?.profile)}
              </h2>
              
              {/* Media Usage Info */}
              {!usageLoading && (
                <div className="bg-gray-800/80 rounded-lg p-4 mb-6" data-testid="media-usage-info">
                  <div className="flex items-center justify-center gap-2 text-sm text-gray-300 mb-2">
                    <Clock className="w-4 h-4" />
                    <span>Session Limit: {Math.floor(effectiveVideoSessionSeconds / 60)} min</span>
                  </div>
                  <div className="text-xs text-gray-400">
                    Daily remaining: {Math.floor(videoDailyRemainingSeconds / 60)} min
                    {mediaTier !== 'free' && (
                      <span className="ml-2 text-rose-400 capitalize">
                        ({mediaTier.replace('_', ' ')})
                      </span>
                    )}
                  </div>
                </div>
              )}
              
              {/* Daily Limit Reached Warning */}
              {!canStartVideoCall && !usageLoading && (
                <div className="bg-red-900/60 border border-red-500/50 p-4 rounded-lg mb-6" data-testid="daily-limit-reached">
                  <div className="flex items-start space-x-3">
                    <AlertTriangle className="w-5 h-5 text-red-400 mt-0.5 flex-shrink-0" />
                    <div className="text-left">
                      <h3 className="font-semibold text-red-200 mb-1">Daily Limit Reached</h3>
                      <p className="text-red-100 text-sm">
                        You've used all your video minutes for today. 
                        Come back tomorrow or upgrade your plan for more time!
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* Safety Reminder (only show if can start call) */}
              {canStartVideoCall && (
                <div className="bg-yellow-900/50 border border-yellow-500/50 p-4 rounded-lg mb-6">
                  <div className="flex items-start space-x-3">
                    <AlertTriangle className="w-5 h-5 text-yellow-400 mt-0.5 flex-shrink-0" />
                    <div className="text-left">
                      <h3 className="font-semibold text-yellow-200 mb-1">Safety Reminder</h3>
                      <p className="text-yellow-100 text-sm">
                        This is a video chat within Unity Love. Never share personal information 
                        like your address or financial details during video calls.
                      </p>
                    </div>
                  </div>
                </div>
              )}
              
              <button
                onClick={startCall}
                disabled={!canStartVideoCall || usageLoading}
                className={`px-8 py-4 rounded-full flex items-center space-x-2 mx-auto transition-all ${
                  canStartVideoCall && !usageLoading
                    ? 'bg-green-500 hover:bg-green-600 text-white cursor-pointer'
                    : 'bg-gray-600 text-gray-400 cursor-not-allowed'
                }`}
                data-testid="start-call-btn"
              >
                <Video className="w-5 h-5" />
                <span>{canStartVideoCall ? 'Start Video Call' : 'Limit Reached'}</span>
              </button>
            </div>
          </div>
        )}

        {/* Connecting State */}
        {isConnecting && (
          <div className="absolute inset-0 bg-black/80 flex items-center justify-center">
            <div className="text-center text-white">
              <div className="spinner w-12 h-12 border-4 border-white border-t-transparent rounded-full mx-auto mb-4 animate-spin"></div>
              <h2 className="font-playfair text-2xl font-bold mb-2">Connecting...</h2>
              <p className="text-gray-300">
                Setting up your video call with {getDisplayName(match?.profile)}
              </p>
            </div>
          </div>
        )}
      </div>

      {/* Call Controls */}
      {isCallActive && (
        <div className="absolute bottom-0 left-0 right-0 bg-black/80 p-6">
          {/* Time Warning Banner */}
          {sessionTimeRemaining <= WARNING_THRESHOLD_SECONDS && sessionTimeRemaining > 0 && (
            <div className="flex items-center justify-center gap-2 text-red-400 text-sm mb-4 animate-pulse" data-testid="time-warning-banner">
              <Timer className="w-4 h-4" />
              <span>{formatTimeRemaining(sessionTimeRemaining)} remaining</span>
            </div>
          )}
          
          <div className="flex items-center justify-center space-x-6">
            {/* Audio Toggle */}
            <button
              onClick={toggleAudio}
              className={`w-12 h-12 rounded-full flex items-center justify-center ${
                isAudioOn ? 'bg-gray-700 text-white' : 'bg-red-500 text-white'
              }`}
              data-testid="toggle-audio-btn"
            >
              {isAudioOn ? <Mic className="w-6 h-6" /> : <MicOff className="w-6 h-6" />}
            </button>
            
            {/* End Call */}
            <button
              onClick={endCall}
              className="w-16 h-16 bg-red-500 hover:bg-red-600 rounded-full flex items-center justify-center text-white"
              data-testid="end-call-btn"
            >
              <PhoneOff className="w-8 h-8" />
            </button>
            
            {/* Video Toggle */}
            <button
              onClick={toggleVideo}
              className={`w-12 h-12 rounded-full flex items-center justify-center ${
                isVideoOn ? 'bg-gray-700 text-white' : 'bg-red-500 text-white'
              }`}
              data-testid="toggle-video-btn"
            >
              {isVideoOn ? <Video className="w-6 h-6" /> : <VideoOff className="w-6 h-6" />}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default VideoChat;
